import React, { useState } from "react";
import { Icons } from "./Icons";

/**
 * DocumentCard - Grid card matching reference design.
 * Renders actual uploaded document content as thumbnail via scaled viewer preview.
 * Falls back to paper silhouettes if no public file path exists or on load error.
 */
const DocumentCard = ({ req, index, onView, onDelete, isAdmin, onSelectMoaVariant }) => {
  const [iframeError, setIframeError] = useState(false);
  const filePath = req.template_file_path || "";
  const lowerPath = filePath.split("?")[0].toLowerCase();

  const isPdf = lowerPath.endsWith(".pdf");
  const isDoc = lowerPath.endsWith(".docx") || lowerPath.endsWith(".doc");
  const isImage = /\.(jpeg|jpg|png|gif|webp)$/.test(lowerPath);
  const isPublicUrl = filePath.startsWith("http://") || filePath.startsWith("https://");

  const docNameLower = (req.document_name || "").toLowerCase();

  // Determine specific document category layout for tailored silhouettes fallback
  const isTableDoc = /inventory|financial|statement|checklist|gpoa|budget|summary|program/.test(docNameLower);
  const isLetterDoc = /letter|application|request|waiver|endorsement|itinerary|travel/.test(docNameLower);

  // File badge color & icon matching reference style
  const getFileBadge = () => {
    if (isPdf) {
      return (
        <div className="w-6 h-6 rounded shrink-0 bg-[#F40F02] text-white flex items-center justify-center">
          <span className="font-bold text-[11px]">PDF</span>
        </div>
      );
    }
    if (isImage) {
      return (
        <div className="w-6 h-6 rounded shrink-0 bg-[#0F9D58] text-white flex items-center justify-center">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
        </div>
      );
    }
    // Default Word style
    return (
      <div className="w-6 h-6 rounded shrink-0 bg-[#4285F4] text-white flex items-center justify-center">
        <span className="font-bold text-[13px] leading-none">W</span>
      </div>
    );
  };

  // Fallback: Tailored Document-Type Paper Silhouette
  const renderPaperSilhouette = () => {
    return (
      <div className="w-full h-full bg-white flex flex-col p-4 shadow-[inset_0_0_8px_rgba(0,0,0,0.05)] border-x border-gray-50">
        <div className="flex items-center space-x-2 border-b border-gray-200 pb-2 mb-3">
          <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-bold shrink-0">
            Doc
          </div>
          <div className="flex-1 space-y-1">
            <div className="h-2 bg-gray-300 rounded w-3/4"></div>
            <div className="h-1 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
        <div className="space-y-1.5 opacity-60">
          <div className="h-1.5 bg-gray-200 rounded w-full"></div>
          <div className="h-1.5 bg-gray-200 rounded w-full"></div>
          <div className="h-1.5 bg-gray-200 rounded w-11/12"></div>
          <div className="h-1.5 bg-gray-200 rounded w-full"></div>
          <div className="h-1.5 bg-gray-200 rounded w-4/5"></div>
        </div>
      </div>
    );
  };

  const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(filePath)}&embedded=true`;

  return (
    <div
      onClick={() => onView && onView(req)}
      className="bg-white rounded border border-[#dadce0] hover:border-transparent hover:shadow-[0_1px_3px_1px_rgba(60,64,67,0.15),0_1px_2px_0_rgba(60,64,67,0.3)] transition-all overflow-hidden flex flex-col cursor-pointer group relative h-[280px]"
    >
      {/* Top Thumbnail Preview Area */}
      <div className="bg-[#f1f3f4] h-[190px] w-full relative overflow-hidden border-b border-[#dadce0]">
        {/* If Image: Render actual image */}
        {isImage && isPublicUrl ? (
          <div className="w-full h-full bg-white relative">
            <img
              src={filePath}
              alt={req.document_name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : isDoc && isPublicUrl && !iframeError ? (
          /* Word doc preview */
          <div className="w-full h-full bg-white relative">
            <div className="absolute w-[800px] h-[1050px] left-1/2 top-0 -translate-x-1/2 -mt-[35px] scale-[0.38] origin-top transform pointer-events-none select-none bg-white">
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(filePath)}`}
                title={req.document_name}
                onError={() => setIframeError(true)}
                className="w-full h-full border-0 bg-white"
                loading="lazy"
              />
            </div>
          </div>
        ) : isPdf && isPublicUrl && !iframeError ? (
          /* PDF preview */
          <div className="w-full h-full bg-white relative">
            <div className="absolute w-[850px] h-[1150px] left-1/2 top-0 -translate-x-1/2 -mt-[55px] scale-[0.35] origin-top transform pointer-events-none select-none bg-white">
              <iframe
                src={viewerUrl}
                title={req.document_name}
                onError={() => setIframeError(true)}
                className="w-full h-full border-0 bg-white"
                loading="lazy"
              />
            </div>
          </div>
        ) : (
          /* Fallback Silhouette */
          renderPaperSilhouette()
        )}

        {/* Preview overlay hint on hover */}
        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none" />
      </div>

      {/* Bottom Information Card Area */}
      <div className="p-3 flex flex-col justify-between flex-1 bg-white">
        <div>
          {req.isMoaGroup ? (
            <div className="mb-1" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#086C53]">MOA (1 of 3 Options)</span>
              </div>
              <select
                value={req.required_document_id}
                onChange={(e) => onSelectMoaVariant && onSelectMoaVariant(Number(e.target.value))}
                className="w-full bg-[#f8fafc] border border-gray-300 rounded px-1.5 py-1 text-[11px] font-semibold text-gray-800 focus:outline-none focus:border-[#086C53] cursor-pointer truncate"
              >
                {req.moaVariants.map((v) => (
                  <option key={v.required_document_id} value={v.required_document_id}>
                    {v.document_name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <h4 className="font-bold text-gray-800 text-[13px] leading-tight group-hover:text-[#086C53] transition-colors line-clamp-2 mb-2 min-h-[34px]">
              {req.document_name}
            </h4>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
          {/* File Badge & Category */}
          <div className="flex items-center space-x-2">
            {getFileBadge()}
            <span className="text-[12px] text-gray-500 font-medium capitalize truncate">
              {req.category === "offcampus" ? "Off-Campus" : req.category}
            </span>
          </div>

          {/* Right Action Button */}
          <div className="flex items-center">
            {isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(req);
                }}
                className="text-gray-400 hover:text-red-600 transition-colors p-1.5 rounded-md hover:bg-red-50"
                title="Delete requirement"
              >
                <Icons.Trash className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
